import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getSEOConfig } from '../config/seoConfig';
import { useCountry } from '../contexts/CountryContext';

/**
 * SEOHead Component
 *
 * Dynamically updates page title and meta tags based on current route and country.
 * Uses vanilla JavaScript to update the DOM (no react-helmet dependency).
 *
 * Usage: Place once in App.tsx inside Router
 */
const SEOHead: React.FC = () => {
  const location = useLocation();
  const { isUSA } = useCountry();

  useEffect(() => {
    // Get SEO config for current route and country
    const seoConfig = getSEOConfig(location.pathname, isUSA);

    // Update page title
    document.title = seoConfig.title;

    // Helper function to update or create meta tag
    const updateMetaTag = (selector: string, attribute: string, content: string) => {
      let element = document.querySelector(selector) as HTMLMetaElement;
      if (!element) {
        element = document.createElement('meta');
        if (selector.includes('property=')) {
          element.setAttribute('property', selector.split('"')[1]);
        } else if (selector.includes('name=')) {
          element.setAttribute('name', selector.split('"')[1]);
        }
        document.head.appendChild(element);
      }
      element.setAttribute(attribute, content);
    };

    // Update standard meta tags
    updateMetaTag('meta[name="description"]', 'content', seoConfig.description);
    updateMetaTag('meta[name="keywords"]', 'content', seoConfig.keywords);

    // Update Open Graph tags
    updateMetaTag('meta[property="og:title"]', 'content', seoConfig.ogTitle);
    updateMetaTag('meta[property="og:description"]', 'content', seoConfig.ogDescription);
    updateMetaTag('meta[property="og:url"]', 'content', seoConfig.canonical);
    if (seoConfig.ogImage) {
      updateMetaTag('meta[property="og:image"]', 'content', seoConfig.ogImage);
    }

    // Update Twitter Card tags
    const twitterTitle = seoConfig.twitterTitle || seoConfig.ogTitle;
    const twitterDescription = seoConfig.twitterDescription || seoConfig.ogDescription;
    updateMetaTag('meta[property="twitter:title"]', 'content', twitterTitle);
    updateMetaTag('meta[property="twitter:description"]', 'content', twitterDescription);
    updateMetaTag('meta[property="twitter:url"]', 'content', seoConfig.canonical);
    if (seoConfig.ogImage) {
      updateMetaTag('meta[property="twitter:image"]', 'content', seoConfig.ogImage);
    }

    // Update canonical link
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', seoConfig.canonical);

    // Update hreflang tags (tells Google about US/CA variants)
    updateHreflangTags(seoConfig.canonical);

    // Update structured data (JSON-LD) for calculators
    updateStructuredData(location.pathname, seoConfig, isUSA);

  }, [location.pathname, isUSA]);

  return null; // This component doesn't render anything
};

/**
 * Update hreflang tags for US/CA language variants
 * Tells Google the same content exists for different regions
 */
const updateHreflangTags = (canonicalUrl: string) => {
  // Remove existing hreflang tags
  document.querySelectorAll('link[hreflang]').forEach(el => el.remove());

  const baseUrl = 'https://larryshelp.org';
  const path = canonicalUrl.replace(baseUrl, '');

  // Pages that have both US and CA variants
  const sharedPaths = ['/', '/car-comparison', '/housing', '/career-comparison', '/forecast', '/pricing'];
  // US-only pages
  const usOnlyPaths = ['/us-retirement'];
  // CA-only pages
  const caOnlyPaths = ['/rrsp', '/income-tax'];

  const addHreflang = (hreflang: string, href: string) => {
    const link = document.createElement('link');
    link.setAttribute('rel', 'alternate');
    link.setAttribute('hreflang', hreflang);
    link.setAttribute('href', href);
    document.head.appendChild(link);
  };

  if (sharedPaths.includes(path)) {
    addHreflang('en-CA', `${baseUrl}${path}`);
    addHreflang('en-US', `${baseUrl}${path}`);
    addHreflang('x-default', `${baseUrl}${path}`);
  } else if (usOnlyPaths.includes(path)) {
    addHreflang('en-US', `${baseUrl}${path}`);
  } else if (caOnlyPaths.includes(path)) {
    addHreflang('en-CA', `${baseUrl}${path}`);
  }
};

/**
 * FAQ data for info pages (used for rich snippets in Google search results)
 */
const infoPageFAQs: Record<string, Array<{ question: string; answer: string }>> = {
  '/car-calculator-info': [
    { question: "How do I compare leasing vs buying a car?", answer: "Enter the car price, lease terms, and financing details. The calculator compares total cost of ownership including depreciation, fuel, insurance, and maintenance over your chosen time period." },
    { question: "Does the calculator compare electric vs gas cars?", answer: "Yes. Enter fuel type for each vehicle and the calculator compares fuel costs, CO₂ emissions, and total ownership costs including charging vs gas station costs." },
    { question: "What costs does the car calculator include?", answer: "Purchase price or lease payments, financing interest, depreciation, fuel/electricity, insurance, maintenance, and opportunity cost of your down payment investment." }
  ],
  '/housing-calculator-info': [
    { question: "Should I rent or buy a house in Canada?", answer: "It depends on home prices, rent costs, how long you plan to stay, and investment returns. Our calculator compares total costs over 20+ years including mortgage interest, property taxes, maintenance, and the opportunity cost of your down payment." },
    { question: "What costs does the rent vs buy calculator include?", answer: "For buying: mortgage payments, property taxes, maintenance, insurance, closing costs, and home appreciation. For renting: monthly rent, renter's insurance, and investment returns on savings you would have spent on a down payment." },
    { question: "How long do I need to own a home for buying to be worth it?", answer: "Typically 5-7 years to break even on closing costs and transaction fees, but it varies by market. The calculator shows your exact break-even point based on your local costs." }
  ],
  '/retirement-planner-info': [
    { question: "When should I take CPP - age 60, 65, or 70?", answer: "Our Monte Carlo optimizer runs 10,000+ scenarios to find your optimal CPP timing based on your specific finances, health, and other income sources. Delaying to 70 gives 42% more than taking at 65, but isn't always optimal." },
    { question: "What is Monte Carlo retirement planning?", answer: "Instead of assuming fixed returns, Monte Carlo simulation randomizes investment returns, inflation, and spending each year across thousands of scenarios. This shows the range of possible outcomes — from worst case to best case — so you can plan for uncertainty." },
    { question: "How does the optimizer find the best retirement strategy?", answer: "It tests different combinations of CPP/OAS timing, RRSP drawdown amounts, and pension splitting across 10,000+ randomized scenarios, then selects the strategy that performs best across good and bad market conditions." }
  ],
  '/career-comparison-info': [
    { question: "How do I compare two job offers?", answer: "Enter salary, benefits, pension/401k matching, commute costs, and expected growth for each career. The calculator projects lifetime earnings and total compensation over 30 years." },
    { question: "Does it account for taxes?", answer: "Yes. The calculator applies federal and provincial/state tax brackets to give you after-tax comparisons, including the tax impact of different benefit structures." }
  ],
  '/personal-forecast-info': [
    { question: "What is a personal financial forecast?", answer: "A year-by-year projection of your income, expenses, savings, and net worth over 30+ years. It accounts for salary growth, inflation, investment returns, and major life events." },
    { question: "How accurate is the forecast?", answer: "The forecast uses your actual numbers and reasonable assumptions for growth rates. It's most useful for comparing scenarios (e.g., saving more vs spending more) rather than predicting exact future values." }
  ]
};

/**
 * Breadcrumb data for all pages
 */
const getBreadcrumbs = (pathname: string, title: string): Array<{ name: string; url: string }> => {
  const baseUrl = 'https://larryshelp.org';
  const crumbs = [{ name: 'Home', url: baseUrl }];

  if (pathname === '/') return crumbs;

  // Info pages get a parent calculator crumb
  const infoToParent: Record<string, { name: string; path: string }> = {
    '/car-calculator-info': { name: 'Car Comparison', path: '/car-comparison' },
    '/housing-calculator-info': { name: 'Housing Calculator', path: '/housing' },
    '/retirement-planner-info': { name: 'Retirement Planner', path: '/us-retirement' },
    '/career-comparison-info': { name: 'Career Comparison', path: '/career-comparison' },
    '/personal-forecast-info': { name: 'Personal Forecast', path: '/forecast' },
  };

  if (infoToParent[pathname]) {
    const parent = infoToParent[pathname];
    crumbs.push({ name: parent.name, url: `${baseUrl}${parent.path}` });
  }

  // Add current page
  const shortTitle = title.split('|')[0].split('-')[0].trim();
  crumbs.push({ name: shortTitle, url: `${baseUrl}${pathname}` });

  return crumbs;
};

/**
 * Update structured data (JSON-LD) based on page type and country
 */
const updateStructuredData = (pathname: string, seoConfig: any, isUSA: boolean) => {
  // Remove existing calculator-specific structured data
  const existingScript = document.querySelector('script[data-page-ld-json]');
  if (existingScript) {
    existingScript.remove();
  }

  const structuredDataItems: object[] = [];
  const country = isUSA ? 'United States' : 'Canada';
  const currency = isUSA ? 'USD' : 'CAD';
  const baseUrl = 'https://larryshelp.org';

  // 1. Breadcrumb schema for all pages (except homepage)
  const breadcrumbs = generateBreadcrumbs(pathname);
  if (breadcrumbs.length > 1) {
    structuredDataItems.push({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": breadcrumbs.map((item, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": item.name,
        "item": item.url
      }))
    });
  }

  // 2. Calculator/App pages - WebApplication schema
  if (pathname.includes('calculator') || pathname.includes('comparison') ||
      pathname.includes('forecast') || pathname.includes('retirement') ||
      pathname === '/housing' || pathname === '/rrsp' || pathname === '/income-tax') {
    structuredDataItems.push({
      "@context": "https://schema.org",
      "@type": "WebApplication",
      "name": seoConfig.title.split('|')[0].trim(),
      "url": seoConfig.canonical,
      "description": seoConfig.description,
      "applicationCategory": "FinanceApplication",
      "operatingSystem": "Web Browser",
      "browserRequirements": "Requires JavaScript",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": currency
      },
      "audience": {
        "@type": "Audience",
        "geographicArea": {
          "@type": "Country",
          "name": country
        }
      },
      "author": {
        "@type": "Organization",
        "name": "Larry's Help",
        "url": baseUrl
      }
    });
  }

  // 3. Info pages - Article + FAQPage schema
  if (pathname.includes('-info')) {
    structuredDataItems.push({
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": seoConfig.title.split('|')[0].trim(),
      "description": seoConfig.description,
      "url": seoConfig.canonical,
      "datePublished": "2025-01-01",
      "dateModified": new Date().toISOString().split('T')[0],
      "author": {
        "@type": "Organization",
        "name": "Larry's Help",
        "url": baseUrl
      },
      "publisher": {
        "@type": "Organization",
        "name": "Larry's Help",
        "logo": {
          "@type": "ImageObject",
          "url": `${baseUrl}/logo512.png`
        }
      },
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": seoConfig.canonical
      }
    });

    // Add FAQPage schema for info pages
    const faqs = getFAQsForPage(pathname, isUSA);
    if (faqs.length > 0) {
      structuredDataItems.push({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqs.map(faq => ({
          "@type": "Question",
          "name": faq.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.answer
          }
        }))
      });
    }
  }

  // 4. Scheduling pages - SoftwareApplication schema
  if (pathname.includes('scheduling') || pathname.includes('/book/')) {
    structuredDataItems.push({
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "Cal.larryshelp.org - Scheduling Platform",
      "description": "Free scheduling and booking platform. Create booking links, sync calendars, and let clients schedule meetings automatically.",
      "url": `${baseUrl}/scheduling`,
      "applicationCategory": "BusinessApplication",
      "operatingSystem": "Web Browser",
      "offers": [
        {
          "@type": "Offer",
          "name": "Free",
          "price": "0",
          "priceCurrency": "CAD",
          "description": "Booking links, calendar sync, reminders, group scheduling, meeting polls"
        },
        {
          "@type": "Offer",
          "name": "Supporters Club",
          "price": "10",
          "priceCurrency": "CAD",
          "description": "Hide branding, API keys, staff logins, payment collection. One-time $10 CAD per year, no auto-renewal."
        }
      ],
      "featureList": [
        "Shareable booking links",
        "Google Calendar sync",
        "Microsoft Outlook integration",
        "Automatic timezone detection",
        "Email reminders",
        "Group scheduling",
        "Meeting polls",
        "Round-robin assignment",
        "Embed widget"
      ]
    });
  }

  // 5. Pricing page - Offer schema
  if (pathname === '/pricing') {
    structuredDataItems.push({
      "@context": "https://schema.org",
      "@type": "Product",
      "name": "Larry's Help Supporters Club",
      "description": "One membership for everything: unlimited saved calculator scenarios, bookkeeping for up to 10 businesses, scheduling Pro features, and priority support. One-time $10 CAD per year, no auto-renewal.",
      "offers": {
        "@type": "Offer",
        "price": "10",
        "priceCurrency": currency,
        "priceValidUntil": new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        "availability": "https://schema.org/InStock",
        "seller": {
          "@type": "Organization",
          "name": "Larry's Help"
        }
      }
    });
  }

  // Add all structured data items to the page
  if (structuredDataItems.length > 0) {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-page-ld-json', 'true');
    script.textContent = JSON.stringify(
      structuredDataItems.length === 1 ? structuredDataItems[0] : structuredDataItems
    );
    document.head.appendChild(script);
  }

  // FAQ schema for info pages (separate script tag for Google rich snippets)
  const existingFaq = document.querySelector('script[data-faq-ld-json]');
  if (existingFaq) existingFaq.remove();

  const faqs = infoPageFAQs[pathname];
  if (faqs) {
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqs.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      }))
    };
    const faqScript = document.createElement('script');
    faqScript.type = 'application/ld+json';
    faqScript.setAttribute('data-faq-ld-json', 'true');
    faqScript.textContent = JSON.stringify(faqSchema);
    document.head.appendChild(faqScript);
  }

  // Breadcrumb schema for all non-homepage pages
  const existingBreadcrumb = document.querySelector('script[data-breadcrumb-ld-json]');
  if (existingBreadcrumb) existingBreadcrumb.remove();

  if (pathname !== '/') {
    const breadcrumbs = getBreadcrumbs(pathname, seoConfig.title);
    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": breadcrumbs.map((crumb, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": crumb.name,
        "item": crumb.url
      }))
    };
    const breadcrumbScript = document.createElement('script');
    breadcrumbScript.type = 'application/ld+json';
    breadcrumbScript.setAttribute('data-breadcrumb-ld-json', 'true');
    breadcrumbScript.textContent = JSON.stringify(breadcrumbSchema);
    document.head.appendChild(breadcrumbScript);
  }
};

/**
 * Generate breadcrumb items for a given path
 */
const generateBreadcrumbs = (pathname: string): { name: string; url: string }[] => {
  const baseUrl = 'https://larryshelp.org';
  const breadcrumbs = [{ name: 'Home', url: baseUrl }];

  const pathMap: Record<string, string> = {
    'car-comparison': 'Car Calculator',
    'car-calculator-info': 'Car Guide',
    'housing': 'Housing Calculator',
    'housing-calculator-info': 'Housing Guide',
    'career-comparison': 'Career Calculator',
    'career-comparison-info': 'Career Guide',
    'retirement-planner-info': 'Retirement Guide',
    'us-retirement': 'Retirement Planner',
    'forecast': 'Financial Forecast',
    'rrsp': 'RRSP Calculator',
    'income-tax': 'Tax Calculator',
    'income-tax-info': 'Tax Guide',
    'accounting': 'Accounting',
    'accounting-info': 'Accounting Guide',
    'scheduling': 'Scheduling',
    'pricing': 'Pricing',
    'account': 'My Account'
  };

  const segments = pathname.split('/').filter(Boolean);
  let currentPath = '';

  segments.forEach(segment => {
    currentPath += `/${segment}`;
    const name = pathMap[segment] || segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ');
    breadcrumbs.push({ name, url: `${baseUrl}${currentPath}` });
  });

  return breadcrumbs;
};

/**
 * Get FAQs for info pages to add FAQPage schema
 */
const getFAQsForPage = (pathname: string, isUSA: boolean): { question: string; answer: string }[] => {
  const faqs: Record<string, { question: string; answer: string }[]> = {
    '/car-calculator-info': [
      {
        question: "Should I lease or buy a car?",
        answer: "It depends on your driving habits, financial situation, and preferences. Leasing typically offers lower monthly payments but no ownership. Buying costs more upfront but you own the vehicle. Our calculator compares both options over 5-10 years."
      },
      {
        question: "Is buying an electric car cheaper than gas?",
        answer: "Electric cars often have lower operating costs (fuel and maintenance) but higher purchase prices. Our calculator compares total cost of ownership including electricity vs gas costs, maintenance, insurance, and depreciation."
      },
      {
        question: "How do I calculate the true cost of car ownership?",
        answer: "True cost includes: purchase price or lease payments, fuel/electricity, insurance, maintenance, depreciation, financing interest, and opportunity cost of your down payment. Our calculator factors in all these costs."
      }
    ],
    '/housing-calculator-info': [
      {
        question: isUSA ? "Should I rent or buy a house in the US?" : "Should I rent or buy a house in Canada?",
        answer: isUSA
          ? "It depends on home prices, mortgage rates, how long you'll stay, and your financial situation. Generally, if you plan to stay 5+ years and can afford a 20% down payment, buying often makes sense. Our calculator compares both options."
          : "It depends on home prices, mortgage rates, how long you'll stay, and your financial situation. In expensive markets like Toronto and Vancouver, renting may be financially better. Our calculator compares both options with real numbers."
      },
      {
        question: "How much house can I afford?",
        answer: "A common guideline is spending no more than 28-32% of gross income on housing costs (mortgage, taxes, insurance). Our calculator helps you see the full picture including opportunity costs of your down payment."
      },
      {
        question: "What costs should I consider when buying a home?",
        answer: "Beyond the purchase price: mortgage interest, property taxes, home insurance, maintenance (1-3% of home value annually), closing costs, and the opportunity cost of your down payment if invested elsewhere."
      }
    ],
    '/retirement-planner-info': [
      {
        question: isUSA ? "When should I claim Social Security?" : "When should I take CPP?",
        answer: isUSA
          ? "You can claim Social Security between ages 62-70. Each year you delay increases benefits by about 8%. Our Monte Carlo calculator runs 10,000 scenarios to find your optimal claiming age based on health, income needs, and survival probabilities."
          : "You can start CPP between ages 60-70. Taking it at 60 means 36% less than at 65, while waiting until 70 gives you 42% more. Our Monte Carlo calculator runs 10,000 scenarios to find your optimal timing."
      },
      {
        question: "What is Monte Carlo retirement simulation?",
        answer: "Monte Carlo simulation runs thousands of scenarios with varying market returns, inflation, and lifespans to show the range of possible outcomes. Instead of one projection, you see probabilities - like a 90% chance of success."
      },
      {
        question: isUSA ? "How much do I need to retire in the US?" : "How much do I need to retire in Canada?",
        answer: "A common rule is 25x your annual expenses. If you need $50,000/year, that's $1.25 million. But this varies based on retirement age, expected returns, and government benefits. Our calculator personalizes this for your situation."
      }
    ],
    '/career-comparison-info': [
      {
        question: "How do I compare two job offers?",
        answer: "Consider: base salary, bonus potential, benefits (health insurance, retirement matching), work-life balance, career growth, commute costs, and job security. Our calculator helps quantify these factors over 30 years."
      },
      {
        question: "Is a higher salary always better?",
        answer: "Not necessarily. A $10,000 higher salary might be offset by worse benefits, longer commute, or less growth potential. Our calculator shows lifetime earnings considering all factors."
      }
    ],
    '/income-tax-info': [
      {
        question: isUSA ? "How do US tax brackets work?" : "How do Canadian tax brackets work?",
        answer: isUSA
          ? "US uses marginal tax brackets - you only pay the higher rate on income above each threshold. For example, in 2025, you pay 10% on income up to $11,600, then 12% on the next portion, etc."
          : "Canada uses marginal tax brackets - you only pay the higher rate on income above each threshold. Combined with provincial taxes, your marginal rate depends on where you live and how much you earn."
      },
      {
        question: isUSA ? "What's the difference between standard and itemized deductions?" : "What are the main tax deductions in Canada?",
        answer: isUSA
          ? "Standard deduction is a fixed amount ($14,600 for single filers in 2025). Itemized deductions let you deduct specific expenses like mortgage interest, state taxes, and charitable donations if they exceed the standard deduction."
          : "Key Canadian deductions include: RRSP contributions (reduces taxable income), childcare expenses, moving expenses for work, and certain medical expenses. Each has specific rules and limits."
      }
    ]
  };

  return faqs[pathname] || [];
};

export default SEOHead;
